import type { ReactNode } from 'react';
import { Icon, cn, type IconName } from '@chessmate/ui';
import { CardBack } from '../PlayingCard.js';

export interface SeatPlateProps {
  readonly testid: string;
  readonly name: string;
  /** Portrait under /public, or null: the plate then shows `fallbackIcon` (the player's own buddy). */
  readonly portrait?: string | null;
  readonly fallbackIcon?: IconName;
  /** A score or points pill, e.g. "12". */
  readonly score?: string | number;
  readonly scoreLabel?: string;
  /** Face-down cards still held, drawn as a card back and a count. */
  readonly cardsLeft?: number;
  readonly cardsLeftLabel?: string;
  /** Rings the plate: it is this seat's move. */
  readonly active?: boolean;
  readonly you?: boolean;
  /** A small line above the name, e.g. the seat ("West"). */
  readonly tag?: string;
  /** What the character just said (text is always the source of truth; speech is optional). */
  readonly bubble?: string | null;
  readonly children?: ReactNode;
  readonly className?: string;
}

/** One player at the table: face, name, score, cards left and (when they speak) a bubble. */
export function SeatPlate({
  testid,
  name,
  portrait,
  fallbackIcon = 'Bot',
  score,
  scoreLabel,
  cardsLeft,
  cardsLeftLabel,
  active = false,
  you = false,
  tag,
  bubble,
  children,
  className,
}: SeatPlateProps) {
  return (
    <section
      aria-label={name}
      data-testid={testid}
      data-active={active || undefined}
      className={cn('relative flex min-w-0 max-w-full flex-col items-center gap-1.5', className)}
    >
      {bubble ? (
        <p
          className="glass glass-light max-w-[16rem] rounded-2xl px-3 py-1.5 text-center text-sm font-semibold text-fg shadow-md lg:text-base"
          data-testid={`${testid}-bubble`}
          aria-live="polite"
        >
          {bubble}
        </p>
      ) : null}
      <div
        className={cn(
          'flex max-w-full items-center gap-3 rounded-2xl border bg-surface/90 py-2 pl-2 pr-4 shadow-md backdrop-blur transition-shadow',
          active ? 'border-accent ring-4 ring-accent/40 shadow-lg' : 'border-border',
          you && 'bg-accent-soft/90',
        )}
      >
        <span className="relative flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-accent ring-2 ring-white/70 sm:size-16 lg:size-20">
          {portrait ? (
            <img src={portrait} alt="" aria-hidden="true" className="size-full object-cover" />
          ) : (
            <Icon name={fallbackIcon} size="lg" className="scale-125 text-accent-fg" />
          )}
        </span>
        <span className="flex min-w-0 flex-col">
          {tag ? <span className="text-xs font-extrabold uppercase tracking-wide text-fg-muted">{tag}</span> : null}
          <span className="truncate font-display text-base font-bold text-fg lg:text-xl" data-testid={`${testid}-name`}>
            {name}
          </span>
          <span className="flex items-center gap-2">
            {score !== undefined ? (
              <span
                className="rounded-full bg-sun/30 px-2 py-0.5 text-sm font-extrabold text-fg lg:text-base"
                data-testid={`${testid}-score`}
                aria-label={scoreLabel}
              >
                {score}
              </span>
            ) : null}
            {cardsLeft !== undefined ? (
              <span className="flex items-center gap-1" aria-label={cardsLeftLabel}>
                <CardBack className="!w-5 sm:!w-5 lg:!w-6" />
                <span className="text-sm font-bold text-fg-muted lg:text-base">×{cardsLeft}</span>
              </span>
            ) : null}
          </span>
        </span>
      </div>
      {children}
    </section>
  );
}
